import { fishingKindTag, loaTag, longlinerTag, vesselTypeCategory } from '#details/constants';
import { RecordValidator, useGenericValidator } from '#record/validator';
import { DetailContext } from '#submission/context';
import { RecordIssue } from '#submission/validator';
import { useVID } from '#vid/model';
import { useT } from 'apprise-frontend-core/intl/language';
import { useTagCache } from 'apprise-frontend-tags/cache';
import format from 'date-fns/format';
import isAfter from 'date-fns/isAfter';
import startOfDay from 'date-fns/startOfDay';

import { useContext } from 'react';

import { useAppSettings } from '#settings/settings';
import { priorTrxauthzType } from '#trxauth.prior/constants';
import { PriorTrxAuthPatch } from '#trxauth.prior/model';
import { isDelisted } from '#record/model';


export const usePriorTrxAuthzValidation = (): RecordValidator<PriorTrxAuthPatch> => {

    const validator = usePriorTrxAuthzValidator()

    return useGenericValidator(priorTrxauthzType, validator)
}


export const usePriorTrxAuthzValidator = (): RecordValidator<PriorTrxAuthPatch> => {

    const t = useT()

    const tagmap = useTagCache()
    const vid = useVID()

    const formatString = useAppSettings().resolveFormat()

    const { currentRecords } = useContext(DetailContext)

    const now = startOfDay(Date.now())

        
    return data => data.reduce((acc, { patch }) => {

        const issues: RecordIssue[] = []

        const current = patch.uvi ? currentRecords[patch.uvi] : undefined


        const currentFromDate = current ? startOfDay(new Date(current.authorization.from)) : undefined
        const currentToDate = current ? startOfDay(new Date(current.authorization.to)) : undefined

        if (current) {

            const uvi = vid.valueOf(patch.uvi)

            if (current.details.vesselKind && current.details.vesselKind !== fishingKindTag)  // wrong type.
                issues.push({ message: t('valid.invalid_priortrx_kind', { uvi, type: tagmap.id2name(current.details.vesselType).of(vesselTypeCategory) }), type: 'error', location: 'uvi' })

            else if (current.details.vesselType !== longlinerTag)  // wrong type.
                issues.push({ message: t('valid.invalid_priortrx_type', { uvi, type: tagmap.id2name(current.details.vesselType).of(vesselTypeCategory) }), type: 'error', location: 'uvi' })


            else if (current.details.specifications[loaTag] && current.details.specifications[loaTag] < 24) {  // is delisted.
                issues.push({ message: t('valid.invalid_priortrx_length', { uvi, length: current.details.specifications[loaTag] }), type: 'error', location: 'uvi' })
            }

            if (isDelisted(current)) {  // is delisted.
                issues.push({ message: t('valid.invalid_priortrx_delisted', { uvi }), type: 'error', location: 'uvi' })
            }


            const currentTo = startOfDay(Date.parse(current?.authorization.to))

            if (isAfter(now, currentTo))  // expired
                issues.push({ message: t('valid.invalid_priortrx_expired', { to: format(currentTo, formatString) }), type: 'warning', location: 'range' })

        }

        patch.priortrxauthz.authzs.forEach((authz,i) => {

            const { from, to } = authz

            const fromDate =  startOfDay(new Date(from))
            const toDate = startOfDay(new Date(to))
       
            const range =  {from: from ? format(fromDate, formatString) : '', to: to ? format(toDate, formatString) : '' }


            if (!from)
                issues.push({ message: t('valid.invalid_priortrx_missing_from', range), type: 'error', location: 'from-'+i })

            if (!to)
                issues.push({ message: t('valid.invalid_priortrx_missing_to', range), type: 'error', location: 'to-'+i })


            if (from && to && isAfter(fromDate,toDate))
                issues.push({ message: t('valid.invalid_priortrx_range', range), type: 'error', location: 'range-'+i })

            if (to && isAfter(now, toDate))
                issues.push({ message: t('valid.invalid_priortrx_pastrange', range), type: 'warning', location: 'range-'+i })


            if (current) {

                if (to && isAfter(toDate, currentToDate!))  // exceeds
                    issues.push({ message: t('valid.invalid_priortrx_to', { ...range, end: format(currentToDate!, formatString) }), type: 'warning', location: 'to-'+i })

                if (from && isAfter(currentFromDate!, fromDate))  // preempts
                    issues.push({ message: t('valid.invalid_priortrx_from', { ...range, start: format(currentFromDate!, formatString)  }), type: 'warning', location: 'from-'+i })
            
            }

        })

        return { ...acc, [patch.id]: issues }

    }, {})

}


